import { motion } from 'framer-motion';
import { Building2, Sparkles } from 'lucide-react';
import { useLanguage } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

export default function About() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen">
      <section className="relative bg-charcoal text-charcoal-foreground py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(${IMAGES.ARABIC_PATTERN_2})`,
            backgroundSize: '300px 300px',
            backgroundRepeat: 'repeat',
          }}
        />
        <motion.div
          className="container mx-auto px-4 relative z-10"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-primary font-semibold text-sm">{t.about.badge}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.about.hero.title}</h1>
            <div className="w-24 h-1 bg-primary mx-auto mb-6 rounded-full" />
            <p className="text-xl text-charcoal-foreground/80">{t.about.hero.subtitle}</p>
          </motion.div>
        </motion.div>
      </section>

      {/* Founder's Quote Section */}
      <section className="relative bg-cream py-24">
        <motion.div
          className="container mx-auto px-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={staggerContainer}
        >
          <div className="grid md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            {/* Photo */}
            <motion.div variants={staggerItem} className="relative flex justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl blur-2xl" />
              <div className="relative w-72 h-80 md:w-80 md:h-96 rounded-2xl overflow-hidden shadow-2xl border-4 border-primary/20">
                <img
                  src={IMAGES.IMG_4276_41}
                  alt="Aaron Liao"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-primary text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg whitespace-nowrap">
                Aaron Liao — Founder
              </div>
            </motion.div>
            {/* Quote */}
            <motion.div variants={staggerItem} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                <Building2 className="w-5 h-5 text-primary" />
                <span className="text-primary font-semibold text-sm">{t.about.founderQuote.badge}</span>
              </div>
              <div className="space-y-4 text-foreground leading-relaxed text-base">
                <p>{t.about.founderQuote.paragraph1}</p>
                <p>{t.about.founderQuote.paragraph2}</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <section className="relative bg-background py-24">
        <motion.div
          className="container mx-auto px-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {t.about.philosophy.title}
              <span className="text-primary">{t.about.philosophy.titleHighlight}</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t.about.philosophy.subtitle}
            </p>
          </motion.div>
          <motion.div variants={staggerItem} className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
              <div className="relative bg-card border border-border rounded-2xl p-8 hover:border-primary/40 transition-all duration-300 h-full">
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <div className="text-3xl">🌙</div>
                </div>
                <h3 className="text-2xl font-bold mb-4">{t.about.philosophy.moon.title}</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {t.about.philosophy.moon.description}
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 font-bold">•</span>
                    <span className="text-sm text-muted-foreground">{t.about.philosophy.moon.point1}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 font-bold">•</span>
                    <span className="text-sm text-muted-foreground">{t.about.philosophy.moon.point2}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 font-bold">•</span>
                    <span className="text-sm text-muted-foreground">{t.about.philosophy.moon.point3}</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
              <div className="relative bg-card border border-border rounded-2xl p-8 hover:border-primary/40 transition-all duration-300 h-full">
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <div className="text-3xl">🌸</div>
                </div>
                <h3 className="text-2xl font-bold mb-4">{t.about.philosophy.plum.title}</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {t.about.philosophy.plum.description}
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 font-bold">•</span>
                    <span className="text-sm text-muted-foreground">{t.about.philosophy.plum.point1}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 font-bold">•</span>
                    <span className="text-sm text-muted-foreground">{t.about.philosophy.plum.point2}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 font-bold">•</span>
                    <span className="text-sm text-muted-foreground">{t.about.philosophy.plum.point3}</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>


    </div>
  );
}